import { useEffect } from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/auth";
import { Spinner } from "@/ui";
import { applyTheme, DEFAULT_THEME } from "@/themes";
import Onboarding from "@/pages/Onboarding";
import Home from "@/pages/Home";
import Chat from "@/pages/Chat";
import Settings from "@/pages/Settings";
import Paywall from "@/pages/Paywall";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Recover from "@/pages/Recover";
import AutoLogin from "@/pages/AutoLogin";
import Portal from "@/pages/Portal";
import PayForFriend from "@/pages/PayForFriend";
import Social from "@/pages/Social";
import SocialRoom from "@/pages/SocialRoom";
import SocialJoin from "@/pages/SocialJoin";
import AppleCallback from "@/pages/AppleCallback";
import Enroll, { StudentRelogin } from "@/pages/Enroll";
import SessionTimer from "@/components/SessionTimer";

function ThemeSync() {
  useEffect(() => {
    // MAXx is dark-navy only — force the dark class regardless of system setting.
    document.documentElement.classList.add("dark");
  }, []);
  return null;
}

function TextSizeSync() {
  const { account } = useAuth();
  useEffect(() => {
    const size = account?.settings?.text_size;
    const px = size === "Small" ? "14px" : size === "Large" ? "18px" : "16px";
    document.documentElement.style.fontSize = px;
  }, [account?.settings?.text_size]);
  return null;
}

function ThemeColorSync() {
  const { account } = useAuth();
  useEffect(() => {
    applyTheme(account?.settings?.color_theme || DEFAULT_THEME);
  }, [account?.settings?.color_theme]);
  return null;
}

function Gate() {
  const { booting, account, role } = useAuth();
  if (booting)
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <Spinner className="h-8 w-8 text-foreground" />
      </div>
    );
  if (!account) return <Navigate to="/onboarding" replace />;
  if (role === "user") return <Navigate to={account.onboarding_complete ? "/home" : "/onboarding?step=tour"} replace />;
  if (role === "parent") return <Navigate to="/portal/parent" replace />;
  if (role === "affiliate") return <Navigate to="/portal/affiliate" replace />;
  if (role === "developer") return <Navigate to="/portal/developer" replace />;
  return <Navigate to="/onboarding" replace />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeSync />
        <TextSizeSync />
        <ThemeColorSync />
        <SessionTimer />
        <Toaster position="bottom-center" />
        <Routes>
          <Route path="/" element={<Gate />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/home" element={<Home />} />
          <Route path="/chat" element={<Chat />} />
          <Route path="/social" element={<Social />} />
          <Route path="/social/join/:token" element={<SocialJoin />} />
          <Route path="/social/:roomId" element={<SocialRoom />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/paywall" element={<Paywall />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/pay-for-friend/:code" element={<PayForFriend />} />
          <Route path="/recover" element={<Recover />} />
          <Route path="/auth/apple/callback" element={<AppleCallback />} />
          <Route path="/login" element={<AutoLogin />} />
          <Route path="/portal/*" element={<Portal />} />
          <Route path="/enroll/s/:token" element={<StudentRelogin />} />
          <Route path="/enroll/:key" element={<Enroll />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
